import { useAuth } from "@clerk/clerk-react";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { useLayoutEffect, useMemo } from "react";

import { Queries } from "@/constants";
import { State } from "@/state";

export function useScopeSetup() {
  // Every page is wrapped in the scope, the public ones too: a visitor who
  // isn't signed in has no user or businesses to ask for.
  const { isSignedIn } = useAuth();
  const { data: userData, loading: isLoadingUser } = useQuery(
    Queries.USER_QUERY,
    {
      fetchPolicy: "network-only",
      skip: !isSignedIn,
    },
  );
  const { data: entityData, loading: isLoadingAccounts } = useQuery(
    Queries.ACCOUNTS_QUERY,
    {
      fetchPolicy: "network-only",
      skip: !isSignedIn,
    },
  );
  const isLoading = isLoadingUser || isLoadingAccounts;

  // Memoize State selectors to prevent unnecessary re-renders
  const scopedActiveEntityIdentifier = State(
    (state) =>
      state.scopedEntityIdentifier ?? state.activeEntityIdentifiers?.[0],
  );
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );

  const user = userData?.user?.data;
  const accounts = entityData?.accounts?.data;

  // Memoize expensive computations
  const entities = useMemo(
    () => accounts?.map((account) => account.entity),
    [accounts],
  );
  const entityIdentifiers = useMemo(
    () => entities?.map((entity) => entity?.identifier),
    [entities],
  );

  useLayoutEffect(() => {
    // If changing the active entity identifiers, and the scoped active entity identifier is not in the new active entity identifiers,
    // then set the scoped active entity identifier to the first active entity identifier
    if (
      activeEntityIdentifiers.length > 0 &&
      !activeEntityIdentifiers.includes(scopedActiveEntityIdentifier)
    ) {
      State.setState({
        scopedEntityIdentifier: activeEntityIdentifiers[0],
      });
    }
  }, [activeEntityIdentifiers, scopedActiveEntityIdentifier]);

  useLayoutEffect(() => {
    if (isLoading || !user?.accounts) {
      return;
    }

    const backendEntityIdentifiers: string[] = user.accounts
      .filter((account) => account.entity?.identifier)
      .map((account) => account.entity!.identifier);
    const backendEntities = user.accounts
      .map((account) => account.entity)
      .filter((entity): entity is NonNullable<typeof entity> => entity != null);
    const backendIdSet = new Set(backendEntityIdentifiers);

    // Prune persisted active identifiers down to ones the backend still knows
    // about. If nothing overlaps (e.g. the DB was reseeded and UUIDs changed,
    // or a different user signed in), fall back to selecting every entity.
    const prunedActive = activeEntityIdentifiers.filter((id) =>
      backendIdSet.has(id),
    );
    const nextActive =
      prunedActive.length > 0 ? prunedActive : backendEntityIdentifiers;

    const nextScoped = backendIdSet.has(scopedActiveEntityIdentifier)
      ? scopedActiveEntityIdentifier
      : nextActive[0];

    const activeChanged =
      nextActive.length !== activeEntityIdentifiers.length ||
      nextActive.some((id, i) => id !== activeEntityIdentifiers[i]);
    const currentUserIdentifier = State.getState().userIdentifier;
    const userChanged = currentUserIdentifier !== user.identifier;
    const scopedChanged = nextScoped !== scopedActiveEntityIdentifier;

    if (activeChanged || userChanged || scopedChanged) {
      State.setState({
        activeEntityIdentifiers: nextActive,
        entities: backendEntities,
        userIdentifier: user.identifier,
        ...(scopedChanged ? { scopedEntityIdentifier: nextScoped } : {}),
      });
    }
  }, [
    user,
    entityIdentifiers,
    activeEntityIdentifiers,
    scopedActiveEntityIdentifier,
    isLoading,
  ]);
}

export function useScope() {
  const { data: userData, loading: isLoadingUser } = useQuery(
    Queries.USER_QUERY,
    {},
  );
  const { data: entityData, loading: isLoadingAccounts } = useQuery(
    Queries.ACCOUNTS_QUERY,
    {},
  );

  // Memoize State selectors to prevent unnecessary re-renders
  const scopedActiveEntityIdentifier = State(
    (state) =>
      state.scopedEntityIdentifier ?? state.activeEntityIdentifiers?.[0],
  );
  const activeEntityIdentifiers = State(
    (state) => state.activeEntityIdentifiers,
  );

  const user = userData?.user?.data;
  const accounts = entityData?.accounts?.data;

  // Memoize expensive computations
  const entities = useMemo(
    () => accounts?.map((account) => account.entity),
    [accounts],
  );
  const entityIdentifiers = useMemo(
    () => entities?.map((entity) => entity?.identifier) ?? [],
    [entities],
  );

  // Memoize filtered results
  const userAccount = useMemo(
    () => accounts?.find((account) => !account.isEntityAccount) ?? null,
    [accounts],
  );
  const userEntity = useMemo(() => userAccount?.entity, [userAccount]);
  const nonUserEntityAccounts = useMemo(
    () => accounts?.filter((account) => account.isEntityAccount) ?? [],
    [accounts],
  );
  const nonUserEntities = useMemo(
    () => nonUserEntityAccounts?.map((account) => account.entity) ?? [],
    [nonUserEntityAccounts],
  );
  const activeAccounts = useMemo(
    () =>
      accounts?.filter((account) =>
        activeEntityIdentifiers.includes(account?.entity?.identifier),
      ) ?? [],
    [accounts, activeEntityIdentifiers],
  );
  const activeEntities = useMemo(
    () =>
      entities?.filter((entity) =>
        activeEntityIdentifiers.includes(entity?.identifier),
      ) ?? [],
    [entities, activeEntityIdentifiers],
  );
  const scopedActiveEntity = useMemo(
    () =>
      entities?.find(
        (entity) => entity?.identifier === scopedActiveEntityIdentifier,
      ) ?? null,
    [entities, scopedActiveEntityIdentifier],
  );
  const scopedActiveAccount = useMemo(
    () =>
      accounts?.find(
        (account) =>
          account?.entity?.identifier === scopedActiveEntityIdentifier,
      ) ?? null,
    [accounts, scopedActiveEntityIdentifier],
  );

  const activeEntityIdentifier = activeEntityIdentifiers?.[0];
  const activeEntity = activeEntities?.[0] ?? null;
  const isLoading = isLoadingUser || isLoadingAccounts;

  // Memoize the return object to prevent unnecessary re-renders of consuming components
  return useMemo(
    () => ({
      accounts,
      activeAccounts,
      activeEntities,
      activeEntity,
      activeEntityIdentifier,
      activeEntityIdentifiers,
      scopedActiveAccount,
      scopedActiveEntity,
      scopedActiveEntityIdentifier,
      entities,
      entityIdentifiers,
      nonUserEntities,
      nonUserEntityAccounts,
      user,
      userEntity,
      isLoadingUser,
      isLoadingAccounts,
      isLoading,
    }),
    [
      accounts,
      activeAccounts,
      activeEntities,
      activeEntity,
      activeEntityIdentifier,
      activeEntityIdentifiers,
      scopedActiveAccount,
      scopedActiveEntity,
      scopedActiveEntityIdentifier,
      entities,
      entityIdentifiers,
      nonUserEntities,
      nonUserEntityAccounts,
      user,
      userEntity,
      isLoadingUser,
      isLoadingAccounts,
      isLoading,
    ],
  );
}
